import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { HelmetProvider } from 'react-helmet-async';
import * as Sentry from '@sentry/react';
import './index.css';
import './i18n/config'; // initialise i18next (side-effect import)
import './pwa/registerSW'; // register service worker (PWA, offline fallback, update prompt)
import App from './App';
import { initPostHog } from './lib/posthog';

// ─── PostHog ──────────────────────────────────────────────────────────────────
initPostHog();

// ─── Sentry ───────────────────────────────────────────────────────────────────
const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    // VITE_APP_ENV is injected by build-web.sh ("prod" | "dev"). Vite's MODE is
    // "production" for both stacks, so it cannot separate staging from live.
    environment: import.meta.env.VITE_APP_ENV || import.meta.env.MODE,
    // Git SHA of the source this bundle was built from, injected by build-web.sh.
    release: import.meta.env.VITE_APP_RELEASE || undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: false }),
    ],
    // 20% of transactions sampled — stays under 10K/mo free limit
    tracesSampleRate: 0.2,
    // 10% session replays on errors only
    replaysOnErrorSampleRate: 0.1,
    replaysSessionSampleRate: 0,
  });
}

const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <HelmetProvider>
      <GoogleOAuthProvider clientId={googleClientId}>
        <BrowserRouter>
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            }
          >
            <App />
          </Suspense>
        </BrowserRouter>
      </GoogleOAuthProvider>
    </HelmetProvider>
  </StrictMode>,
);
